import liveTuning from '../../data/goal-line-defense.json';

type FormationSide = 'offense' | 'defense';

type FormationSpot = {
  x: number;
  y: number;
};

const GOAL_LINE_FIELD_X = 110;
const GOAL_LINE_SHELL_START_YARDS = 15;
const GOAL_LINE_SHELL_FULL_YARDS = 7;

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

function roleProfile(roleKey: string): {
  targetDepth: number;
  planeBuffer: number;
  minimumDepth: number;
  targetWidth: (baseWidth: number) => number;
} | null {
  const role = roleKey.toUpperCase();
  if (role.includes('DE') || role.includes('DT')) {
    return {
      targetDepth: 0.68,
      planeBuffer: 0.3,
      minimumDepth: 0.62,
      targetWidth: (baseWidth) => baseWidth * 1.02,
    };
  }
  if (role === 'LB1' || role === 'LB3' || role === 'WILL' || role === 'SAM') {
    return {
      targetDepth: 0.76,
      planeBuffer: 0.42,
      minimumDepth: 0.66,
      targetWidth: (baseWidth) => Math.sign(baseWidth || (role === 'LB1' || role === 'WILL' ? -1 : 1)) * 6.35,
    };
  }
  if (role === 'LB2' || role === 'MIKE') {
    return {
      targetDepth: 1.85,
      planeBuffer: 0.55,
      minimumDepth: 0.78,
      targetWidth: (baseWidth) => baseWidth * 0.4,
    };
  }
  if (role.includes('CB')) {
    return {
      targetDepth: 0.98,
      planeBuffer: 0.7,
      minimumDepth: 0.62,
      targetWidth: (baseWidth) => baseWidth,
    };
  }
  if (role === 'SS' || role === 'S2') {
    return {
      targetDepth: 2.25,
      planeBuffer: 0.55,
      minimumDepth: 0.78,
      targetWidth: (baseWidth) => Math.sign(baseWidth || 1) * Math.max(4.8, Math.min(6.4, Math.abs(baseWidth))),
    };
  }
  if (role === 'FS' || role === 'S1') {
    return {
      targetDepth: 3.45,
      planeBuffer: 0.45,
      minimumDepth: 0.82,
      targetWidth: (baseWidth) => baseWidth * 0.78,
    };
  }
  return null;
}

/**
 * Progressively turns a normal defensive shell into a six-man goal-line front
 * as the offense enters the low red zone. The two outside linebackers walk onto
 * the line, the middle linebacker stays free to scrape, the corners press, and
 * the safeties cap the scoring plane. This changes physical starting positions
 * only; blocks, routes, pursuit, and outcomes are still resolved by the live
 * arcade simulation.
 */
export function goalLineDefensiveFormationSpot(
  anchorX: number,
  side: FormationSide,
  roleKey: string,
  baseSpot: FormationSpot,
): FormationSpot {
  if (side !== 'defense') return baseSpot;
  const profile = roleProfile(roleKey);
  if (!profile) return baseSpot;

  const yardsToGoal = GOAL_LINE_FIELD_X - anchorX;
  if (yardsToGoal > GOAL_LINE_SHELL_START_YARDS) return baseSpot;

  const intensity = clamp(
    (GOAL_LINE_SHELL_START_YARDS - yardsToGoal)
      / (GOAL_LINE_SHELL_START_YARDS - GOAL_LINE_SHELL_FULL_YARDS),
    0,
    1,
  );
  const deepestLegalOffset = Math.max(
    profile.minimumDepth,
    yardsToGoal - profile.planeBuffer,
  );
  const calledDepth = Math.min(profile.targetDepth, deepestLegalOffset);
  const blendedDepth = baseSpot.x + (calledDepth - baseSpot.x) * intensity;
  const calledWidth = profile.targetWidth(baseSpot.y);

  return {
    x: Math.min(blendedDepth, deepestLegalOffset),
    y: baseSpot.y + (calledWidth - baseSpot.y) * intensity,
  };
}

export const goalLineDefenseTuning = {
  startYards: GOAL_LINE_SHELL_START_YARDS,
  fullYards: GOAL_LINE_SHELL_FULL_YARDS,
};

export function usesGoalLineAssignments(anchorX: number): boolean {
  const yardsToGoal = GOAL_LINE_FIELD_X - anchorX;
  return yardsToGoal > 0 && yardsToGoal <= liveTuning.liveAssignmentYards;
}

type RunFitPlayer = FormationSpot & {
  roleKey: string;
  side: FormationSide;
  vx: number;
  vy: number;
  state: string;
  engagementState: string | null;
  blockPartner: string | null;
  assignmentKind: string | null;
  ratings: Record<string, number>;
  attributes: Record<string, number>;
};

/** Called only after possession is established, never from a planned run lane.
 * Changes assignments, not positions, collision reach, or tackle probabilities.
 */
export function applyGoalLineRunFits<T extends RunFitPlayer>(options: {
  playersByRole: Map<string, T>;
  runner: T;
  anchorX: number;
  frameInRun: number;
  fieldCenterY: number;
  preserveLinebackerFits?: boolean;
  steerToward: (player: T, target: FormationSpot, speed: number, acceleration: number) => void;
}): boolean {
  if (!usesGoalLineAssignments(options.anchorX) || options.runner.side !== 'offense') return false;
  const { runner, anchorX, frameInRun, fieldCenterY, steerToward } = options;
  const tuning = liveTuning.run;
  for (const role of ['WILL', 'MIKE', 'SAM', 'LCB', 'RCB', 'FS', 'SS']) {
    if (options.preserveLinebackerFits && ['WILL', 'MIKE', 'SAM'].includes(role)) continue;
    const defender = options.playersByRole.get(role);
    if (!defender || defender.state === 'down' || defender.blockPartner
      || ['engaging', 'screened', 'tackled', 'tackling'].includes(defender.engagementState || '')) continue;
    const safety = role === 'FS' || role === 'SS';
    const corner = role.includes('CB');
    const awareness = clamp((defender.ratings.awareness ?? 50) / 100, 0, 1);
    const motor = clamp(defender.attributes.runSupportMotor ?? defender.attributes.pursuitDiscipline ?? 0.5, 0, 1);
    const readSkill = awareness * 0.7 + motor * 0.3;
    const readFrames = Math.round(tuning.maximumReadFrames
      - (tuning.maximumReadFrames - tuning.minimumReadFrames) * readSkill
      + (corner ? tuning.cornerReadFrames : safety ? tuning.safetyReadFrames : 0));

    if (frameInRun < readFrames) {
      // Read the mesh without first bailing to a midfield landmark. Braking is
      // still performed by the ordinary rated steering, not a position clamp.
      defender.assignmentKind = 'goal-line-run-read';
      steerToward(defender, defender, tuning.minimumPursuitSpeed, tuning.minimumPursuitAcceleration);
      continue;
    }

    const gap = Math.hypot(runner.x - defender.x, runner.y - defender.y);
    const lookahead = Math.min(tuning.maximumLookaheadSeconds,
      gap / Math.max(1, defender.attributes.maxSpeed ?? 6)) * readSkill;
    const nearContact = clamp(gap / tuning.contactApproachYards, 0, 1);
    const side = role === 'WILL' || role === 'LCB' || role === 'FS' ? -1 : role === 'MIKE' ? 0 : 1;
    const runnerSide = Math.sign(runner.y - fieldCenterY);
    const outsideFit = side !== 0 && side === runnerSide && (corner || role === 'WILL' || role === 'SAM');
    const leverage = (outsideFit ? tuning.edgeLeverageYards : tuning.cutbackLeverageYards) * side * nearContact;
    // Safeties protect the alley/plane behind the fit, rather than charging
    // into the same backfield pile as the linebackers. Corners keep the edge.
    const fitFloor = anchorX + (safety ? tuning.safetyFitDepthYards
      : corner ? tuning.cornerFitDepthYards : tuning.linebackerFitDepthYards);
    const holdEdge = (role === 'WILL' || role === 'SAM')
      && runner.x < anchorX + tuning.runnerDeclaredDepthYards
      && Math.abs(runner.y - fieldCenterY) < tuning.edgeReadWidthYards;
    defender.assignmentKind = outsideFit ? 'goal-line-force' : safety ? 'goal-line-alley' : 'goal-line-run-fit';
    steerToward(defender, {
      // Intersect the observed path before the scoring plane, even when only
      // a yard remains. There is no required gain before support can trigger.
      x: Math.min(GOAL_LINE_FIELD_X - tuning.planeBufferYards,
        Math.max(fitFloor, runner.x + Math.max(0, runner.vx) * lookahead)),
      y: holdEdge ? fieldCenterY + side * tuning.edgeReadWidthYards
        : runner.y + runner.vy * lookahead + leverage,
    }, tuning.minimumPursuitSpeed + (tuning.maximumPursuitSpeed - tuning.minimumPursuitSpeed) * motor,
    tuning.minimumPursuitAcceleration + (tuning.maximumPursuitAcceleration - tuning.minimumPursuitAcceleration) * readSkill);
  }
  return true;
}
